import React, {useMemo} from 'react';
import {LicenseTimerWrapper} from "./LicenseTimer.styled";
import {Box, CircularProgress, Stack, Typography} from "@mui/material";
import {formatDateWithLanguageToLongDate, getLicenseTime} from "../../../../../utils/format-date";
import {useTranslation} from "react-i18next";

interface LicenseTimerProps {
    activationDate: string;
    expirationDate: string;
}

const LicenseTimer = ({activationDate, expirationDate}: LicenseTimerProps) => {
    const {t} = useTranslation();

    const size = 240;
    const thickness = 80;

    const {remainingTimePercentage, remainingDaysNumber} = useMemo(
        () => getLicenseTime(activationDate, expirationDate),
        [activationDate, expirationDate]
    );

    return (
        <LicenseTimerWrapper>
            <Typography variant="body1" component="p" fontWeight="medium">
                {t('licenseManager.timer.title')}
            </Typography>

            <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                    variant="determinate"
                    value={100}
                    size={size}
                    thickness={(10 / size) * thickness}
                    sx={{ color: 'action.hover', position: 'absolute' }}
                />

                <CircularProgress
                    variant="determinate"
                    value={remainingTimePercentage}
                    size={size}
                    thickness={(10 / size) * thickness}
                    sx={{ color: 'primary.main' }}
                />

                <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexDirection: 'column',
                    }}
                >
                    <Typography variant="h1" component="div">
                        {remainingDaysNumber}
                    </Typography>

                    <Typography variant="h6" component="div">
                        {t(remainingDaysNumber === 1 ? 'licenseManager.timer.dayRemaining' : 'licenseManager.timer.daysRemaining')}
                    </Typography>

                    <Typography variant="body1" component="div">
                        {t('licenseManager.timer.timeRemainingPercentage', { percentage: remainingTimePercentage })}
                    </Typography>
                </Box>
            </Box>

            <Stack direction="column" spacing={1.5} alignItems="center">
                <Typography variant="body1" component="div" fontWeight="medium">
                    {t('licenseManager.timer.expirationLabel')}
                </Typography>

                <Typography variant="h5" component="div">
                    {formatDateWithLanguageToLongDate(expirationDate)}
                </Typography>
            </Stack>
        </LicenseTimerWrapper>
    );
};

export default LicenseTimer;